import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../../services/api';

const createInitialFormData = () => ({
  name: '',
  target_type: 'http',
  url: [],
  method: 'POST',
  headers: [{ name: '', value: '' }],
  request_body: '',
  message_template: '',
  stream_response: '',
  timeout_ms: 60000,
  request_transform: '',
  response_transform: '',
  session_config: '',
  http_status_code: '',
  auth: false,
  auth_request_body: '',
  login_url: '',
  session_param: '',
  auth_headers: [{ name: '', value: '' }]
});

export default function Endpoints() {
  const [url, setUrl] = useState('');
  const navigate = useNavigate();
  const [hasAuth, setHasAuth] = useState(false);
  const selectedApp = sessionStorage.getItem("selectedApp");

  const [testLoading, setTestLoading] = useState(false);
  const [testResponse, setTestResponse] = useState(null);
  const [transformedResponse, setTransformedResponse] = useState('');
  const [hasTransformedResponse, setHasTransformedResponse] = useState(false);
  const [requestTransformLoading, setRequestTransformLoading] = useState(false);
  const [responseTransformLoading, setResponseTransformLoading] = useState(false);
  const [requestTransformResult, setRequestTransformResult] = useState('');
  const [responseTransformResult, setResponseTransformResult] = useState('');
  const [responseSampleInput, setResponseSampleInput] = useState('');

  const formatResponseForDisplay = (responseValue) => {
    if (typeof responseValue !== 'string') {
      return typeof responseValue === 'object'
        ? JSON.stringify(responseValue, null, 2)
        : String(responseValue ?? '');
    }

    if (responseValue.includes('data:')) {
      const decodedEvents = [];
      const lines = responseValue.split('\n');
      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed.startsWith('data:')) continue;
        const payload = trimmed.slice(5).trim();
        if (!payload) continue;
        try {
          decodedEvents.push(JSON.parse(payload));
        } catch {
          decodedEvents.push(trimmed);
        }
      }

      if (decodedEvents.length > 0) {
        const finalEvent = [...decodedEvents].reverse().find(
          (evt) => evt && typeof evt === 'object' && evt.type === 'final_response' && evt.content
        );
        if (finalEvent) {
          return finalEvent.content;
        }
        return JSON.stringify(decodedEvents, null, 2);
      }
    }

    return responseValue;
  };

  const handleTestEndpoint = async () => {
    setTestLoading(true);
    setTestResponse(null);
    setTransformedResponse('');
    setHasTransformedResponse(false);

    const testUrl = url.trim();
    if (!testUrl) {
      setTestResponse('No valid URL provided.');
      setTestLoading(false);
      return;
    }

    try {
      const { data } = await API.post('/test-endpoint/',
          formData.target_type === 'websocket'
            ? {
                target_type: 'websocket',
                url: testUrl,
                message_template: formData.message_template,
                stream_response: formData.stream_response,
                response_transform: formData.response_transform,
                timeout_ms: formData.timeout_ms,
                headers: formData.headers.reduce((acc, header) => {
                  if (header.name && header.value) acc[header.name] = header.value;
                  return acc;
                }, {}),
              }
            : {
                url: testUrl,
                method: formData.method,
                headers: formData.headers.reduce((acc, header) => {
                  if (header.name && header.value) {
                    acc[header.name] = header.value;
                  }
                  return acc;
                }, {}),
                request_body: formData.request_body,
                request_transform: formData.request_transform,
                response_transform: formData.response_transform
              }
      );

      if (data.success) {
        const formattedResponse = formatResponseForDisplay(data.response);

        setTestResponse(formattedResponse);
        setResponseSampleInput(
          typeof data.response === 'string'
            ? data.response
            : JSON.stringify(data.response ?? '', null, 2),
        );

        if (Object.prototype.hasOwnProperty.call(data, 'transformed_response') && data.transformed_response !== null) {
          const formattedTransform = typeof data.transformed_response === 'object'
            ? JSON.stringify(data.transformed_response, null, 2)
            : String(data.transformed_response);
          setTransformedResponse(formattedTransform);
          setHasTransformedResponse(true);
        }
      } else {
        setTestResponse(`Error: ${data.error}`);
      }
    } catch (err) {
      setTestResponse('Error: ' + (err.response?.data?.error || err.message));
      setTransformedResponse('');
      setHasTransformedResponse(false);
    }
    setTestLoading(false);
  };

  const handleTestRequestTransform = async () => {
    setRequestTransformLoading(true);
    setRequestTransformResult('');
    try {
      const { data } = await API.post('/providers/test-request-transform/', {
          transformCode: formData.request_transform,
          prompt: 'What is the capital of France?',
      });
      if (!data.success) {
        throw new Error(data.error || 'Request transform test failed');
      }

      setRequestTransformResult(
        typeof data.result === 'object'
          ? JSON.stringify(data.result, null, 2)
          : String(data.result),
      );
    } catch (err) {
      setRequestTransformResult(`Error: ${err.response?.data?.error || err.message}`);
    }
    setRequestTransformLoading(false);
  };

  const handleTestResponseTransform = async () => {
    setResponseTransformLoading(true);
    setResponseTransformResult('');
    try {
      const inputToTest = responseSampleInput || testResponse || '';
      const { data } = await API.post('/providers/test-response-transform/', {
          transformCode: formData.response_transform,
          response: inputToTest,
      });
      if (!data.success) {
        throw new Error(data.error || 'Response transform test failed');
      }

      setResponseTransformResult(
        typeof data.result === 'object'
          ? JSON.stringify(data.result, null, 2)
          : String(data.result),
      );
    } catch (err) {
      setResponseTransformResult(`Error: ${err.response?.data?.error || err.message}`);
    }
    setResponseTransformLoading(false);
  };


  const [formData, setFormData] = useState(createInitialFormData);
  const isWebsocket = formData.target_type === 'websocket';

  const [visibleSections, setVisibleSections] = useState({
    request: false,
    response: false,
    sessions: false,
    status: false
  });

  useEffect(() => {
    const savedData = sessionStorage.getItem("targetData");

    if (savedData) {
      const parsedData = JSON.parse(savedData);
      if (selectedApp && parsedData?.name && parsedData.name !== selectedApp) {
        const resetData = { ...createInitialFormData(), name: selectedApp };
        setFormData(resetData);
        setUrl('');
        setHasAuth(false);
        sessionStorage.setItem("targetData", JSON.stringify(resetData));
        return;
      }
      if (parsedData.headers && parsedData.headers.length > 0) {
        const normalized = parsedData.headers.map(h =>
          h.name && h.value
            ? h
            : { name: Object.keys(h)[0], value: Object.values(h)[0] }
        );
        parsedData.headers = normalized;
      }
      setFormData(parsedData);

      // url is saved as an array; take the first non-empty entry
      if (parsedData.url && Array.isArray(parsedData.url)) {
        const firstUrl = parsedData.url.find(u => u && u.trim() !== '') || '';
        setUrl(firstUrl);
      } else if (parsedData.url && typeof parsedData.url === 'string') {
        setUrl(parsedData.url);
      } else {
        setUrl('');
      }

      if (parsedData.auth) {
        setHasAuth(parsedData.auth);
      }
    } else if (selectedApp) {
      setFormData(prev => ({ ...prev, name: selectedApp }));
    }
  }, [selectedApp]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleHeaderChange = (index, key, value) => {
    setFormData(prev => ({
      ...prev,
      headers: prev.headers.map((header, headerIndex) =>
        headerIndex === index ? { ...header, [key]: value } : header
      ),
    }));
  };

  const handleAuthHeaderChange = (index, key, value) => {
    setFormData(prev => ({
      ...prev,
      auth_headers: prev.auth_headers.map((header, headerIndex) =>
        headerIndex === index ? { ...header, [key]: value } : header
      ),
    }));
  };

  const addHeader = () => {
    setFormData(prev => ({
      ...prev,
      headers: [...prev.headers, { name: '', value: '' }]
    }));
  };

  const addAuthHeader = () => {
    setFormData(prev => ({
      ...prev,
      auth_headers: [...prev.auth_headers, { name: '', value: '' }]
    }));
  };

  const toggleSection = (key) => {
    setVisibleSections(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const validateRequestBody = () => {
    return (formData.request_body || '').includes('{{prompt}}');
  };

  // WebSocket messageTemplate must reference the prompt ({{prompt}}, {{ prompt }},
  // or {{ prompt | dump }}), the same idea as the HTTP body's {{prompt}} rule.
  const validateMessageTemplate = () => /\{\{\s*prompt/.test(formData.message_template || '');

  const handleProtocolChange = (proto) => {
    setFormData(prev => ({ ...prev, target_type: proto }));
    if (proto === 'websocket') {
      setHasAuth(false);          // HTTP login-flow auth doesn't apply to WS
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!url.trim()) return;

    try {
      const updatedFormData = {
        ...formData,
        url: url.trim() ? [url.trim()] : []
      };

      sessionStorage.setItem("targetData", JSON.stringify(updatedFormData));
      navigate("/newscan/detectors");
    } catch (err) {
      console.error("Submission error:", err);
    }
  };

  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      auth: hasAuth,
    }));
  }, [hasAuth]);

  useEffect(() => {
    const updatedFormData = {
      ...formData,
      url: url.trim() ? [url.trim()] : [],
    };
    sessionStorage.setItem("targetData", JSON.stringify(updatedFormData));
  }, [formData, url]);

  return (
    <form onSubmit={handleSubmit} className="space-y-6 text-white pb-8">

      {/* Selected Application */}
      <div className="bg-[#101012] border border-[#232325] rounded-2xl p-6">
        <h2 className="text-xl font-bold mb-4 text-white">Selected Application</h2>
        <input
          type="text"
          name="name"
          placeholder="Target name"
          value={formData.name}
          readOnly
          className="w-full bg-[#1a1a1c] text-white border border-[#232325] focus:border-blue-500 rounded-lg px-4 py-3 outline-none"
        />
      </div>

      {/* HTTP Endpoint Configuration */}
      <div className="bg-[#101012] border border-[#232325] rounded-2xl p-6 space-y-5">
        <h2 className="text-xl font-bold text-white">{isWebsocket ? 'WebSocket Endpoint Configuration' : 'HTTP Endpoint Configuration'}</h2>

        {/* Protocol selector */}
        <div>
          <p className="text-gray-400 text-sm mb-3">Target protocol</p>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex items-center cursor-pointer" onClick={() => handleProtocolChange('http')}>
              <div className={`w-4 h-4 rounded-full mr-2 flex-shrink-0 ${!isWebsocket ? 'bg-blue-500' : 'border-2 border-blue-500'}`} />
              <span className="text-white text-sm">HTTP</span>
            </div>
            <div className="flex items-center cursor-pointer" onClick={() => handleProtocolChange('websocket')}>
              <div className={`w-4 h-4 rounded-full mr-2 flex-shrink-0 ${isWebsocket ? 'bg-blue-500' : 'border-2 border-blue-500'}`} />
              <span className="text-white text-sm">WebSocket</span>
            </div>
          </div>
        </div>

        <div>
          <label className="block text-sm text-gray-400 mb-2">{isWebsocket ? 'WebSocket URL' : 'URL'}</label>
          <input
            type={isWebsocket ? 'text' : 'url'}
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            required
            className="w-full bg-[#1a1a1c] text-white border border-[#232325] focus:border-blue-500 rounded-lg px-4 py-3 outline-none break-all"
            placeholder={isWebsocket ? 'ws://localhost:3400/ws' : 'https://example.com/api/chat'}
          />
        </div>

        {/* Method — HTTP only */}
        {!isWebsocket && (
        <div>
          <label className="block text-sm text-gray-400 mb-2">Method</label>
          <select
            name="method"
            value={formData.method}
            onChange={handleInputChange}
            className="w-full bg-[#1a1a1c] text-white border border-[#232325] focus:border-blue-500 rounded-lg px-4 py-3 outline-none"
          >
            <option value="POST">POST</option>
            <option value="GET">GET</option>
          </select>
        </div>
        )}

        {/* Headers */}
        <div>
          <label className="block text-sm text-gray-400 mb-2">Headers</label>
          <div className="space-y-2">
            {formData.headers.map((header, index) => (
              <div key={index} className="flex flex-col sm:flex-row gap-2">
                <input
                  type="text"
                  placeholder="Name"
                  value={header.name}
                  onChange={(e) => handleHeaderChange(index, 'name', e.target.value)}
                  className="w-full bg-[#1a1a1c] text-white border border-[#232325] focus:border-blue-500 rounded-lg px-4 py-3 outline-none"
                />
                <input
                  type="text"
                  placeholder="Value"
                  value={header.value}
                  onChange={(e) => handleHeaderChange(index, 'value', e.target.value)}
                  className="w-full bg-[#1a1a1c] text-white border border-[#232325] focus:border-blue-500 rounded-lg px-4 py-3 outline-none"
                />
              </div>
            ))}
          </div>
          <button type="button" onClick={addHeader} className="mt-3 text-blue-400 hover:text-blue-300 text-sm flex items-center gap-1 transition-colors">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Header
          </button>
        </div>

        {/* Request Body — HTTP only */}
        {!isWebsocket && (
        <div>
          <label className="block text-sm text-gray-400 mb-2">Request Body</label>
          <textarea
            name="request_body"
            rows={4}
            value={formData.request_body}
            onChange={handleInputChange}
            className={`w-full bg-[#1a1a1c] text-white border rounded-lg px-4 py-3 font-mono text-sm outline-none focus:border-blue-500 ${
              !validateRequestBody() && formData.request_body ? 'border-red-500' : 'border-[#232325]'
            }`}
          />
          {!validateRequestBody() && formData.request_body && (
            <p className="text-red-400 text-sm mt-1">Request body must contain {'{{prompt}}'}</p>
          )}
        </div>
        )}

        {/* WebSocket-specific fields */}
        {isWebsocket && (
          <>
            <div>
              <label className="block text-sm text-gray-400 mb-2">Message Template</label>
              <textarea
                name="message_template"
                rows={3}
                value={formData.message_template || ''}
                onChange={handleInputChange}
                placeholder={'{"prompt": {{prompt}}}'}
                className={`w-full bg-[#1a1a1c] text-white border rounded-lg px-4 py-3 font-mono text-sm outline-none focus:border-blue-500 ${
                  !validateMessageTemplate() && formData.message_template ? 'border-red-500' : 'border-[#232325]'
                }`}
              />
              {!validateMessageTemplate() && formData.message_template && (
                <p className="text-red-400 text-sm mt-1">Message template must contain {'{{prompt}}'}</p>
              )}
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-2">
                Stream Response
                <span className="text-gray-600 font-normal"> — JS accumulator for streaming servers (leave blank and use Response Transform below for request/response)</span>
              </label>
              <textarea
                name="stream_response"
                rows={6}
                value={formData.stream_response || ''}
                onChange={handleInputChange}
                placeholder={'function(accumulator, data) {\n  // parse data.data, return [{ output }, done]\n}'}
                className="w-full bg-[#1a1a1c] text-white border border-[#232325] focus:border-blue-500 rounded-lg px-4 py-3 font-mono text-sm outline-none overflow-x-auto"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-2">Timeout (ms)</label>
              <input
                type="number"
                name="timeout_ms"
                value={formData.timeout_ms ?? 60000}
                onChange={handleInputChange}
                className="w-full bg-[#1a1a1c] text-white border border-[#232325] focus:border-blue-500 rounded-lg px-4 py-3 outline-none"
              />
            </div>
          </>
        )}
      </div>

      {/* Advanced Configuration */}
      <div className="bg-[#101012] border border-[#232325] rounded-2xl p-6 space-y-5">
        <h2 className="text-xl font-bold text-white">Advanced Configuration</h2>

        {/* Auth toggle */}
        <div className="flex items-center gap-4">
          <p className="text-white font-medium">Does the endpoint have authentication?</p>

          <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
            <input
              name="auth"
              type="checkbox"
              className="sr-only peer"
              checked={hasAuth}
              onChange={() => setHasAuth(!hasAuth)}
            />
            <div className="w-11 h-6 bg-[#232325] rounded-full peer peer-checked:bg-blue-500 peer-focus:ring-2 peer-focus:ring-blue-500 transition-colors duration-300" />
            <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full shadow-md transition-transform duration-300 transform peer-checked:translate-x-5" />
          </label>

          <span className="text-sm font-medium text-gray-400">
            {hasAuth ? "Yes" : "No"}
          </span>
        </div>

        {hasAuth === true && (
          <div className="space-y-4 pl-4 border-l border-[#232325]">
            <div>
              <label className="block text-sm text-gray-400 mb-2">Login Endpoint URL</label>
              <input
                type="text"
                name="login_url"
                onChange={handleInputChange}
                placeholder="https://example.com/api/login"
                value={formData.login_url}
                className="w-full bg-[#1a1a1c] text-white border border-[#232325] focus:border-blue-500 rounded-lg px-4 py-3 outline-none break-all"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-2">Auth Headers</label>
              <div className="space-y-2">
                {formData.auth_headers.map((header, index) => (
                  <div key={index} className="flex flex-col sm:flex-row gap-2">
                    <input
                      type="text"
                      placeholder="Name"
                      value={header.name}
                      onChange={(e) => handleAuthHeaderChange(index, 'name', e.target.value)}
                      className="w-full bg-[#1a1a1c] text-white border border-[#232325] focus:border-blue-500 rounded-lg px-4 py-3 outline-none"
                    />
                    <input
                      type="text"
                      placeholder="Value"
                      value={header.value}
                      onChange={(e) => handleAuthHeaderChange(index, 'value', e.target.value)}
                      className="w-full bg-[#1a1a1c] text-white border border-[#232325] focus:border-blue-500 rounded-lg px-4 py-3 outline-none"
                    />
                  </div>
                ))}
              </div>
              <button type="button" onClick={addAuthHeader} className="mt-3 text-blue-400 hover:text-blue-300 text-sm flex items-center gap-1 transition-colors">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Auth Header
              </button>
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-2">Session Parameter</label>
              <input
                type="text"
                name="session_param"
                onChange={handleInputChange}
                placeholder="Enter the session parameter which will hold the updated session cookie"
                value={formData.session_param}
                className="w-full bg-[#1a1a1c] text-white border border-[#232325] focus:border-blue-500 rounded-lg px-4 py-3 outline-none"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-2">Auth Request Body</label>
              <textarea
                name="auth_request_body"
                rows={4}
                value={formData.auth_request_body}
                onChange={handleInputChange}
                className={`w-full bg-[#1a1a1c] text-white border rounded-lg px-4 py-3 font-mono text-sm outline-none focus:border-blue-500 ${
                  !formData.auth_request_body ? 'border-red-500' : 'border-[#232325]'
                }`}
              />
            </div>
          </div>
        )}

        {/* Collapsible sections */}
        <CollapsibleSection
          label="Request Transform"
          visible={visibleSections.request}
          toggle={() => toggleSection('request')}
          content={
            <div className="space-y-3 mt-3">
              <textarea
                name="request_transform"
                rows={4}
                value={formData.request_transform}
                onChange={handleInputChange}
                className="w-full bg-[#1a1a1c] text-white border border-[#232325] focus:border-blue-500 rounded-lg px-4 py-3 font-mono text-sm outline-none overflow-x-auto"
              />
              <button
                type="button"
                onClick={handleTestRequestTransform}
                disabled={requestTransformLoading}
                className="bg-blue-500/10 text-blue-400 border border-[#232325] hover:border-blue-500/50 px-4 py-2 rounded-lg text-sm transition-colors disabled:opacity-50"
              >
                {requestTransformLoading ? 'Testing...' : 'Test Request Transform'}
              </button>
              {requestTransformResult && (
                <pre className="whitespace-pre-wrap break-words bg-[#1a1a1c] text-gray-200 border border-[#232325] rounded-lg p-3 text-sm font-mono overflow-x-auto">
                  {requestTransformResult}
                </pre>
              )}
            </div>
          }
        />

        <CollapsibleSection
          label="Response Transform"
          visible={visibleSections.response}
          toggle={() => toggleSection('response')}
          content={
            <div className="space-y-3 mt-3">
              <textarea
                name="response_transform"
                rows={4}
                value={formData.response_transform}
                onChange={handleInputChange}
                className="w-full bg-[#1a1a1c] text-white border border-[#232325] focus:border-blue-500 rounded-lg px-4 py-3 font-mono text-sm outline-none overflow-x-auto"
              />
              <textarea
                rows={5}
                value={responseSampleInput}
                onChange={(e) => setResponseSampleInput(e.target.value)}
                placeholder="Paste sample raw response JSON/text to test the transform"
                className="w-full bg-[#1a1a1c] text-white border border-[#232325] focus:border-blue-500 rounded-lg px-4 py-3 font-mono text-sm outline-none overflow-x-auto"
              />
              <button
                type="button"
                onClick={handleTestResponseTransform}
                disabled={responseTransformLoading}
                className="bg-blue-500/10 text-blue-400 border border-[#232325] hover:border-blue-500/50 px-4 py-2 rounded-lg text-sm transition-colors disabled:opacity-50"
              >
                {responseTransformLoading ? 'Testing...' : 'Test Response Transform'}
              </button>
              {responseTransformResult && (
                <pre className="whitespace-pre-wrap break-words bg-[#1a1a1c] text-green-300 border border-[#232325] rounded-lg p-3 text-sm font-mono overflow-x-auto">
                  {responseTransformResult}
                </pre>
              )}
            </div>
          }
        />

        <CollapsibleSection
          label="Sessions"
          visible={visibleSections.sessions}
          toggle={() => toggleSection('sessions')}
          content={
            <textarea
              name="session_config"
              rows={4}
              value={formData.session_config}
              onChange={handleInputChange}
              className="w-full bg-[#1a1a1c] text-white border border-[#232325] focus:border-blue-500 rounded-lg px-4 py-3 font-mono text-sm outline-none mt-3"
            />
          }
        />

        <CollapsibleSection
          label="HTTP Status Code"
          visible={visibleSections.status}
          toggle={() => toggleSection('status')}
          content={
            <input
              type="text"
              name="http_status_code"
              value={formData.http_status_code}
              onChange={handleInputChange}
              className="w-full bg-[#1a1a1c] text-white border border-[#232325] focus:border-blue-500 rounded-lg px-4 py-3 outline-none mt-3"
            />
          }
        />

        {/* Test Endpoint */}
        <div className="flex justify-end">
          <button
            type="button"
            onClick={handleTestEndpoint}
            disabled={testLoading}
            className="bg-blue-500/10 text-blue-400 border border-[#232325] hover:border-blue-500/50 px-4 py-2 rounded-lg text-sm transition-colors disabled:opacity-50"
          >
            {testLoading ? 'Testing...' : 'Test Endpoint'}
          </button>
        </div>

        {testResponse && (
          <div className="bg-[#1a1a1c] border border-[#232325] rounded-xl p-4 text-sm overflow-y-auto max-h-96">
            <p className="text-gray-400 font-medium mb-2">Response:</p>
            <pre className="whitespace-pre-wrap break-all text-gray-200 font-mono text-sm">{testResponse}</pre>
          </div>
        )}

        {hasTransformedResponse && (
          <div className="bg-[#1a1a1c] border border-[#232325] rounded-xl p-4 text-sm overflow-y-auto max-h-96">
            <p className="text-green-400 font-medium mb-2">Transformed Response:</p>
            <pre className="whitespace-pre-wrap break-all text-green-300 font-mono text-sm">{transformedResponse}</pre>
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="flex flex-col-reverse sm:flex-row justify-between gap-3">
        <button
          type="button"
          onClick={() => navigate('/newscan/context')}
          className="px-6 py-2.5 rounded-lg border border-[#232325] hover:border-blue-500/50 text-gray-400 hover:text-white transition-colors text-sm font-medium"
        >
          Back
        </button>
        <button
          type="submit"
          className="px-6 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white transition-colors text-sm font-medium"
        >
          Next
        </button>
      </div>
    </form>
  );
}

function CollapsibleSection({ label, visible, toggle, content }) {
  return (
    <div className="border border-[#232325] rounded-xl overflow-hidden">
      <div
        className="flex justify-between items-center cursor-pointer px-4 py-3 hover:bg-[#1a1a1c] transition-colors"
        onClick={toggle}
      >
        <span className="text-white font-medium text-sm">{label}</span>
        <span
          className={`text-gray-500 transform transition-transform duration-200 ${visible ? 'rotate-180' : ''}`}
        >
          ▼
        </span>
      </div>
      {visible && <div className="px-4 pb-4">{content}</div>}
    </div>
  );
}
